import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getMyNation, setMyNation, submitNationPulse } from "@/lib/social.functions";
import { getSessionId } from "@/lib/session";
import { useLanguage } from "@/hooks/useLanguage";
import { Flag } from "lucide-react";

const OPTIONS: Array<{ code: string; flag: string; label: string }> = [
  { code: "MEX", flag: "🇲🇽", label: "México" },
  { code: "ARG", flag: "🇦🇷", label: "Argentina" },
  { code: "BRA", flag: "🇧🇷", label: "Brasil" },
  { code: "USA", flag: "🇺🇸", label: "USA" },
  { code: "ESP", flag: "🇪🇸", label: "España" },
  { code: "FRA", flag: "🇫🇷", label: "France" },
  { code: "POR", flag: "🇵🇹", label: "Portugal" },
  { code: "COL", flag: "🇨🇴", label: "Colombia" },
];

export function NationPickerInline() {
  const { t } = useLanguage();
  const qc = useQueryClient();
  const mine = useQuery({ queryKey: ["social", "my_nation"], queryFn: () => getMyNation() });
  const pick = useMutation({
    mutationFn: (v: { code: string; flag: string }) => setMyNation({ data: { nation_code: v.code, nation_flag: v.flag } }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["social", "my_nation"] });
      void submitNationPulse({ data: { nation_code: v.code, session_id: getSessionId(), pulse_count: 1 } }).catch(() => {});
      qc.invalidateQueries({ queryKey: ["social", "nation_rivalry"] });
    },
  });
  const current = mine.data?.nation?.nation_code ?? null;

  return (
    <section className="rounded-2xl border border-border bg-background/60 p-4">
      <header className="mb-2 flex items-center gap-2">
        <Flag className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-black uppercase tracking-wider">{t("nation_pick_title")}</h3>
      </header>
      <p className="mb-3 text-[11px] text-muted-foreground">{t("nation_pick_desc")}</p>
      <div className="flex flex-wrap gap-2">
        {OPTIONS.map((o) => {
          const active = current === o.code;
          return (
            <button
              key={o.code}
              type="button"
              onClick={() => pick.mutate({ code: o.code, flag: o.flag })}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                active ? "border-primary bg-primary/20" : "border-border bg-background hover:border-primary/40"
              }`}
            >
              <span className="text-base">{o.flag}</span>
              <span>{o.label}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}